import { type MaybePromise } from '../types.ts'

/**
 * Adds or removes an item in an array depending on if it is currently in the array.
 * @param array The array of the item.
 * @param item The item that will be added / removed.
 */
export function arrayToggle<T>(array: T[], item: T): void {
  const index = array.indexOf(item)

  if (index >= 0) {
    array.splice(index, 1)
  } else {
    array.push(item)
  }
}

/**
 * Returns a promise that will be resolved after the specified delay.
 * @param ms The amount of milliseconds to delay.
 */
export function delay(ms = 1): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Finds an item in an array and splices the array on it.
 * @param array The array to splice.
 * @param callback The callback to find the item.
 * @param inserts Optionally insert new items at the position of the item.
 */
export function findAndSpliceArray<T>(array: T[], callback: (item: T) => boolean, ...inserts: T[]): void {
  const index = array.findIndex(callback)

  if (index >= 0) {
    array.splice(index, 1, ...inserts)
  }
}

/**
 * Convert a (large) number to a human readable number.
 *
 * // Returns '42 B'
 * humanSize(42)
 * // Returns '1.2 M'
 * humanSize(1234567).
 * @param number Number to convert.
 * @returns Human readable (rounded) string representation of the number.
 */
export function humanNumber(number: number): string {
  let i = 0
  const units = ['', 'K', 'M', 'G', 'T', 'Q']

  while (number > 1000) {
    number = number / 1000
    i++
  }

  let humanNumber = ''

  if (i > 1) {
    humanNumber = Math.max(number, 0.1).toFixed(1)
  } else {
    humanNumber = `${Math.round(number)}`
  }

  return `${humanNumber} ${units[i]}`
}

/**
 * Convert a number representing bytes to a human readable string.
 * @param size Size in bytes.
 * @returns Human readable (rounded) string representation of the size.
 * @example
 *
 * humanSize(1000) // Returns '1000 B'
 * humanSize(1030) // Returns '1 KB'
 * humanSize(1234567) // Returns '1.2 MB'
 */
export function humanSize(size: number): string {
  let i = 0
  const units = ['B', 'KB', 'MB', 'GB', 'TB']

  while (size > 1000) {
    size = size / 1000
    i++
  }

  let humanSize = ''

  if (i > 1) {
    humanSize = Math.max(size, 0.1).toFixed(1)
  } else {
    humanSize = `${Math.round(size)}`
  }

  return `${humanSize} ${units[i]}`
}

const search1 = /([a-z0-9])([A-Z])/g
const search2 = /[ _]+/g
const replace1 = '$1-$2'
const replace2 = '-'
export function kebab(string: string): string {
  const kebab = string.replace(search1, replace1)

  return kebab.replaceAll(search2, replace2).toLowerCase()
}

/**
 * Returns a promise that resolves when `requestAnimationFrame` is called back.
 */
export function nextFrame() {
  return new Promise((resolve) => requestAnimationFrame(resolve))
}

/**
 * Convert a number to px.
 *
 * Examples:
 * - `px(0)` => `'0px'`
 * - `px('5')` => `'5px'`
 * - `px(undefined)` => `undefined`
 * - `px(NaN)` => `NaN`.
 * @param number The amount of pixels.
 */
export function px(number: string | number | undefined): string | number | undefined {
  return number === undefined ? undefined : isNaN(number as number) ? number : `${number}px`
}

/**
 * Creates a hue for the given string.
 * Two strings that are similar do not necessarily get get a similar hue.
 * Useful for assigning unique colors to the given input.
 * @param string The string to convert to a hue.
 */
export function stringToHue(string: string): number {
  let hash = 0

  for (let i = 0; i < string.length; i++) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash)
  }

  return hash % 360
}

/**
 * Check if the given target is found in one of the given selectors.
 * @param target An HTML element.
 * @param selectors Query selectors to match on.
 * @returns A boolean indicating if the target is found or not.
 */
export function targetInOneOfSelectors(target: HTMLElement, selectors: string[] | string): boolean {
  if (typeof selectors === 'string') {
    selectors = [selectors]
  }

  for (const selector of selectors) {
    const ignoredElements = Array.from(document.querySelectorAll(selector))

    if (ignoredElements.some((element) => element.contains(target))) {
      return true
    }
  }

  return false
}

/**
 * Check if an object is empty.
 * @param object The object to check.
 * @returns A boolean indicating if the object is empty.
 */
export function isEmpty(object: object): boolean {
  for (const _ in object) {
    return false
  }

  return true
}

/**
 * Retry the given callback n amount of times.
 * @param times How many times to retry.
 * @param callback The function to retry.
 * @param sleepMilliseconds How many milliseconds to sleep between each retry.
 * @returns The return value of the callback function.
 * @example
 * retry(10, api.get('users/1'), 300)
 */
export async function retry<T = unknown>(times: number, callback: () => MaybePromise<T>, sleepMilliseconds = 0): Promise<T> {
  for (let _ = 0; _ < times; _++) {
    try {
      return await callback()
    } catch (error) {
      if (_ === times - 1) {
        throw error
      }
      await delay(sleepMilliseconds)
    }
  }

  throw Error() // Should never happen, but makes typescript happy :)
}

/**
 * Translate helper to replace counts and placeholders in translation strings.
 * @param string The translation string.
 * @param countOrReplacements The count or replacements to insert into the translation string.
 * @param replacements The replacements to insert into the translation string.
 */
export function t(string: string, countOrReplacements: number | Record<string, string>, replacements?: Record<string, string>) {
  let count: undefined | number = undefined
  if (typeof countOrReplacements === 'number') {
    count = countOrReplacements
  } else {
    replacements = countOrReplacements
  }

  for (const [placeholder, replacement] of Object.entries(replacements ?? {})) {
    string = string.replaceAll(`$${placeholder}`, replacement)
  }

  if (count !== undefined) {
    string = string.replaceAll('$count', String(count))
    const strings = string.split(' | ')
    if (strings.length > 2) {
      return strings[Math.min(count, 2)]
    } else if (strings.length === 2) {
      return strings[count > 1 ? 1 : 0]
    }
  }

  return string
}

/**
 *
 * Indicates if the browser supports plaintext editable content.
 */
export function supportsPlaintextEditableContent(): boolean {
  const div = document.createElement('div')
  div.setAttribute('contenteditable', 'plaintext-only')

  return div.contentEditable === 'plaintext-only'
}

/**
 * Converts an array of objects into an object keyed by `key`.
 * @param key The key to key the objects by.
 * @param array The array that contains the objects.
 * @returns An object keyed by `key`.
 */
export function keyBy<T extends Record<string, any>>(key: keyof T, array: T[]) {
  const result: Record<string, T> = {}

  for (const object of array) {
    result[object[key]] = object
  }

  return result
}
